"use client";

import { Group } from "@visx/group";
import { Pie } from "@visx/shape";
import React, { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import type { TooltipData, YFormatType } from "../../../context/ChartContext";
import { getYFormatSettings } from "../../../utils/charts";
import { mapValuesToCategoricalColors } from "../../../utils/colors";
import { ChartTooltip } from "../ChartTooltip";

export interface PieDataPoint {
  /** Category label */
  label: string;
  /** Numeric value. Non-positive values take no arc — see `shareOf`. */
  value: number;
  /** Optional color override for this slice */
  color?: string;
}

export interface PieChartProps {
  /** Array of data points with labels and values */
  data: PieDataPoint[];
  /** Width of the chart (auto-resizes to container if not provided) */
  width?: number;
  /** Height of the chart (auto-resizes to container if not provided) */
  height?: number;
  /**
   * Hole radius as a fraction of the outer radius. `0` (the default) is a pie;
   * `0.6` is a donut. Values outside 0–0.95 are clamped.
   */
  innerRadiusRatio?: number;
  /** Formatter for slice values (uses the standard chart formatting system) */
  yFormatter?: YFormatType;
  /** Show the label/value/share legend beside the pie (default true) */
  showLegend?: boolean;
  /** Show tooltip on hover (default true) */
  showTooltip?: boolean;
  /**
   * Callback when a slice is clicked. Supplying it turns every slice into a
   * focusable, named control that answers Enter and Space, and the drawing's
   * role from `img` to `group` — an `img` hides the controls inside it.
   */
  onSliceClick?: (dataPoint: PieDataPoint, index: number) => void;
  /** Callback when a slice is hovered */
  onSliceHover?: (dataPoint: PieDataPoint | null, index: number | null) => void;
  /** Additional className */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /**
   * The chart's accessible name. Required, not optional-with-a-default, for the
   * reason `CategoryBarChart` states: a default means every chart of this kind
   * announces identically to a screen reader (ALL-2539, ALL-2715).
   */
  "aria-label": string;
}

/** Gap between slices, in radians. Small enough to read as a seam, not a gap. */
const PAD_ANGLE = 0.005;
/** Below this the legend would take more room than the pie it explains. */
const LEGEND_MIN_WIDTH = 320;

/**
 * A slice's share of the whole.
 *
 * Negative values are clamped to zero rather than drawn: d3's pie layout maps a
 * negative value to a negative angle, which silently overlaps the neighbouring
 * arcs and produces a chart that adds up to more than itself. A part-of-whole
 * mark has no meaning for a negative part, so the honest rendering is no arc —
 * the legend still lists the category at 0%.
 */
function shareOf(value: number): number {
  return value > 0 ? value : 0;
}

/**
 * What a screen reader hears on a focused slice: the same three facts the
 * legend row shows, in the same order — category, value, share.
 */
function sliceName(slice: PieDataPoint, total: number, format: (value: number) => string): string {
  const share = total === 0 ? 0 : (shareOf(slice.value) / total) * 100;
  return `${slice.label}: ${format(slice.value)}, ${share.toFixed(1)}%`;
}

/**
 * PieChart
 *
 * Part-of-whole mark for categorical data: one slice per category, sized by its
 * share of the total, with a legend carrying the value and the percentage. Pass
 * `innerRadiusRatio` for a donut.
 *
 * Input order is preserved — slices are not re-sorted by value, so a producer
 * that ordered its categories deliberately (severity, lifecycle stage) keeps
 * that order.
 *
 * @example
 * ```tsx
 * <PieChart
 *   data={[
 *     { label: "Online", value: 127 },
 *     { label: "Offline", value: 23 },
 *   ]}
 *   aria-label="Devices by status"
 * />
 * ```
 */
export const PieChart: React.FC<PieChartProps> = ({
  data,
  width: propWidth,
  height: propHeight,
  innerRadiusRatio = 0,
  yFormatter = { type: "number" },
  showLegend = true,
  showTooltip = true,
  onSliceClick,
  onSliceHover,
  className,
  style,
  "aria-label": ariaLabel,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipLabel, setTooltipLabel] = useState<string | null>(null);
  const [actualWidth, setActualWidth] = useState(propWidth ?? 600);
  const [actualHeight, setActualHeight] = useState(propHeight ?? 300);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (propWidth !== undefined && propHeight !== undefined) {
      setActualWidth(propWidth);
      setActualHeight(propHeight);
      return;
    }

    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (propWidth === undefined) {
          setActualWidth(entry.contentRect.width);
        }
        if (propHeight === undefined) {
          setActualHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(containerRef.current);
    if (propWidth === undefined) {
      setActualWidth(containerRef.current.offsetWidth);
    }
    if (propHeight === undefined) {
      setActualHeight(containerRef.current.offsetHeight || 300);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [propWidth, propHeight]);

  const total = useMemo(() => data.reduce((sum, d) => sum + shareOf(d.value), 0), [data]);

  const colored = useMemo(() => {
    const categoricalColors = mapValuesToCategoricalColors(data.map((d) => d.label));
    return data.map((d) => ({
      ...d,
      color: d.color || categoricalColors[d.label] || "var(--color-viz-default)",
    }));
  }, [data]);

  const formatSettings = getYFormatSettings(yFormatter);
  // Only a slice that *does* something needs to be a control. `onSliceHover`
  // alone is a hover affordance, not an action, so it does not put every wedge
  // of every pie in the tab order.
  const interactive = onSliceClick !== undefined;
  const legendVisible = showLegend && actualWidth >= LEGEND_MIN_WIDTH;
  const chartWidth = legendVisible ? Math.max(actualWidth * 0.45, LEGEND_MIN_WIDTH * 0.45) : actualWidth;
  const diameter = Math.max(0, Math.min(chartWidth, actualHeight) - 8);
  const radius = diameter / 2;
  const innerRadius = radius * Math.min(Math.max(innerRadiusRatio, 0), 0.95);

  if (data.length === 0 || total === 0) {
    return (
      <div
        ref={containerRef}
        className={twMerge(
          "flex h-full min-h-[200px] w-full items-center justify-center rounded-lg border border-dashed border-border-default bg-background-surface text-sm font-medium text-text-secondary",
          className
        )}
        style={propHeight !== undefined ? { height: propHeight, ...style } : style}
      >
        No data available
      </div>
    );
  }

  const handleSliceHover = (index: number | null, dataPoint: PieDataPoint | null, centroid?: [number, number]) => {
    setHoveredIndex(index);
    onSliceHover?.(dataPoint, index);

    if (index !== null && dataPoint && centroid) {
      const x = chartWidth / 2 + centroid[0];
      const y = actualHeight / 2 + centroid[1];
      setTooltipLabel(dataPoint.label);
      setTooltipData({
        // The tooltip's x is a time axis for time-series charts; a categorical
        // chart passes its label separately and this value is never read.
        xValue: new Date(),
        series: [
          {
            label: "Value",
            value: dataPoint.value,
            color: dataPoint.color || "var(--color-viz-default)",
          },
        ],
        x,
        y,
        position: x > actualWidth / 2 ? "left" : "right",
      });
    } else {
      setTooltipData(null);
      setTooltipLabel(null);
    }
  };

  return (
    <div
      ref={containerRef}
      className={twMerge("relative flex h-full w-full items-center gap-4 rounded-lg bg-background-surface", className)}
      style={propHeight !== undefined ? { height: propHeight, ...style } : style}
    >
      {/* The name goes on the drawing, not on the wrapper: `role="img"` makes an
          element's own text children invisible to assistive tech, and the
          wrapper holds the legend — which is the part a screen reader can
          actually use.

          When the slices are clickable the drawing stops being an image and
          becomes a group of controls, for the same reason: `img` would hide the
          buttons inside it. */}
      <svg
        width={chartWidth}
        height={actualHeight}
        className="flex-shrink-0"
        role={interactive ? "group" : "img"}
        aria-label={ariaLabel}
      >
        <Group top={actualHeight / 2} left={chartWidth / 2}>
          <Pie
            data={colored}
            pieValue={(d) => shareOf(d.value)}
            pieSort={null}
            pieSortValues={null}
            outerRadius={radius}
            innerRadius={innerRadius}
            padAngle={PAD_ANGLE}
          >
            {(pie) =>
              pie.arcs.map((arc, i) => {
                const isActive = hoveredIndex === i || hoveredIndex === null;
                const centroid = pie.path.centroid(arc);
                return (
                  <path
                    key={arc.data.label}
                    d={pie.path(arc) ?? undefined}
                    fill={arc.data.color}
                    opacity={isActive ? 1 : 0.5}
                    style={{
                      cursor: onSliceClick || onSliceHover ? "pointer" : "default",
                      transition: "opacity 150ms ease-out",
                    }}
                    onMouseEnter={() => handleSliceHover(i, arc.data, centroid)}
                    onMouseLeave={() => handleSliceHover(null, null)}
                    onClick={() => onSliceClick?.(arc.data, i)}
                    // A clickable slice is a control, so it is focusable, named,
                    // and answers the keys a button answers. Without this the
                    // whole interaction is mouse-only, and a wedge announces as
                    // nothing at all. A non-interactive pie stays out of the tab
                    // order — it is a picture, and the legend carries the numbers.
                    {...(interactive
                      ? {
                          role: "button",
                          tabIndex: 0,
                          "aria-label": sliceName(arc.data, total, formatSettings.format),
                          onFocus: () => handleSliceHover(i, arc.data, centroid),
                          onBlur: () => handleSliceHover(null, null),
                          onKeyDown: (event: KeyboardEvent<SVGPathElement>) => {
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            onSliceClick?.(arc.data, i);
                          },
                        }
                      : {})}
                  />
                );
              })
            }
          </Pie>
        </Group>
      </svg>

      {legendVisible ? (
        <ul className="flex min-w-0 flex-col gap-1 overflow-y-auto py-2 text-xs">
          {colored.map((d, i) => (
            <li
              key={d.label}
              className="flex items-center gap-2"
              style={{ opacity: hoveredIndex === i || hoveredIndex === null ? 1 : 0.5 }}
              // Highlights the slice this row names — no centroid, so no
              // tooltip: the row already carries the value and the share.
              onMouseEnter={() => handleSliceHover(i, d)}
              onMouseLeave={() => handleSliceHover(null, null)}
            >
              <span className="h-3 w-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: d.color }} />
              <span className="truncate text-text-primary" title={d.label}>
                {d.label}
              </span>
              <span className="ml-auto flex-shrink-0 tabular-nums text-text-secondary">
                {formatSettings.format(d.value)} ({((shareOf(d.value) / total) * 100).toFixed(1)}%)
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {showTooltip && <ChartTooltip data={tooltipData} formatter={yFormatter} label={tooltipLabel ?? undefined} />}
    </div>
  );
};
